import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Empty, Input, Select, message } from "antd";
import dayjs from "dayjs";
import { userMessageForError } from "../../utils/apiError";
import { formatDdMmYyyy, isCapOverdue } from "../../utils/internalAuditView";
import { iaRequest } from "./iaApi";

const { TextArea } = Input;

const CAP_CSV_COLUMNS = [
  "Finding ID",
  "Status",
  "Due Date",
  "Title",
  "Immediate Correction",
  "Corrective Action",
  "RCA Method",
  "Root Cause",
  "Responsible",
  "Lessons Learned",
  "Verification Method",
  "Verification Due",
  "Mgmt Approval",
];

const STATUS_OPTIONS = [
  { value: "Open", label: "Open" },
  { value: "In Progress", label: "In Progress" },
  { value: "Closed", label: "Closed" },
];

const MGMT_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
];

function statusBadge(status) {
  const s = String(status || "Open");
  if (s === "In Progress") {
    return (
      <span className="inline-flex rounded-full border border-[color:var(--color-info-line)] bg-[color:var(--color-info-bg)] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-[color:var(--color-accent)]">
        In Progress
      </span>
    );
  }
  if (s === "Closed") {
    return (
      <span className="inline-flex rounded-full border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-[color:var(--color-ok-fg)]">
        Closed
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-[color:var(--color-warn-fg)]">
      Open
    </span>
  );
}

function capRowsToCsv(rows) {
  const header = CAP_CSV_COLUMNS.join(",");
  const lines = rows.map((r) =>
    [
      r.id,
      r.status,
      formatDdMmYyyy(r.dueDate),
      r.title,
      r.immediateCorrection,
      r.correctiveAction,
      r.rcaMethod,
      r.rootCause,
      r.responsible,
      r.lessonsLearned,
      r.verificationMethod,
      formatDdMmYyyy(r.verificationDue),
      r.mgmtApproval,
    ]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
      .join(","),
  );
  return [header, ...lines].join("\n");
}

/** Date input shown DD-MM-YYYY; a cleared picker sends '' (clears the date). */
function DateField({ row, field, label, enabled, onSave, emptyText }) {
  const shown = formatDdMmYyyy(row[field]);
  if (!enabled) return <span className="text-sm text-[color:var(--color-text-muted-dark)]">{shown || emptyText}</span>;
  const d = row[field] ? dayjs(row[field]) : null;
  return (
    <DatePicker
      size="small"
      aria-label={label}
      format="DD-MM-YYYY"
      placeholder={emptyText}
      allowClear
      value={d?.isValid() ? d : null}
      onChange={(val) => onSave(val ? val.format("YYYY-MM-DD") : "")}
    />
  );
}

function FieldBlock({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--color-text-muted)] mb-1">{label}</div>
      {children}
    </div>
  );
}

/**
 * Corrective Action Plan (CAP) sub-tab — cards for non-conformity findings.
 */
export function CorrectiveActionsSubTab({
  projectId,
  correctiveActions,
  canEdit,
  /** Closing a CAP and recording management approval are Lead Advisor acts (API: platform_admin gets 403 PLATFORM_ADMIN_APPROVAL_DENIED, PRD s2.2). */
  canApprove = false,
  /** Setting due / verification dates: Lead Advisor or Advisor (API: 403 for admin, SPOC, delegate). */
  canSetDates = false,
  getApiToken,
}) {
  const [localRows, setLocalRows] = useState(correctiveActions?.rows || []);
  const lastExternalRef = React.useRef(correctiveActions?.rows);

  useEffect(() => {
    if (correctiveActions?.rows && correctiveActions.rows !== lastExternalRef.current) {
      if (correctiveActions.rows.length !== localRows.length) {
        setLocalRows(correctiveActions.rows);
      }
      lastExternalRef.current = correctiveActions.rows;
    }
  }, [correctiveActions?.rows, localRows.length]);

  // Bumped when a save is refused: remounts the cards so the uncontrolled inputs show the restored values.
  const [revision, setRevision] = useState(0);

  const patchCap = useCallback(async (actionId, patch) => {
    if (!projectId) return;
    const before = localRows.find((r) => r.id === actionId);
    setLocalRows((prev) =>
      prev.map((r) => (r.id === actionId ? { ...r, ...patch } : r)),
    );
    try {
      await iaRequest(
        getApiToken,
        `/api/v1/projects/${projectId}/internal-audit/corrective-actions/${encodeURIComponent(actionId)}`,
        { method: "PATCH", body: patch },
      );
    } catch (e) {
      if (before) {
        const restore = Object.fromEntries(Object.keys(patch).map((k) => [k, before[k]]));
        setLocalRows((prev) => prev.map((r) => (r.id === actionId ? { ...r, ...restore } : r)));
        setRevision((n) => n + 1);
      }
      message.error(userMessageForError(e, "Could not save corrective action."));
    }
  }, [projectId, getApiToken, localRows]);

  const handleDownloadTemplate = useCallback(() => {
    const csv = capRowsToCsv(localRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "corrective-action-plan-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [localRows]);

  const cards = useMemo(() => localRows, [localRows]);

  if (!correctiveActions || localRows.length === 0) {
    return (
      <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-12 shadow-sm">
        <Empty description="Complete Audit Findings and proceed to generate corrective actions." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-6 shadow-sm">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-black tracking-tight text-[color:var(--color-text-hero)]">Corrective Action Plan (CAP)</h3>
            <p className="text-xs text-[color:var(--color-text-muted)] mt-1">
              {cards.length} corrective action{cards.length !== 1 ? "s" : ""} from audit findings
            </p>
          </div>
          <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate} className="text-xs font-bold uppercase">
            Download template
          </Button>
        </div>

        <div className="space-y-4">
          {cards.map((row) => (
            <div
              key={`${row.id}-${revision}`}
              data-cap-id={row.id}
              className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-5 shadow-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold text-[color:var(--color-text-muted-dark)]">{row.id}</span>
                  {canEdit ? (
                    <Select
                      size="small"
                      value={row.status}
                      options={canApprove ? STATUS_OPTIONS : STATUS_OPTIONS.map((o) => (o.value === "Closed" ? { ...o, disabled: true } : o))}
                      className="min-w-[120px]"
                      onChange={(v) => patchCap(row.id, { status: v })}
                    />
                  ) : (
                    statusBadge(row.status)
                  )}
                </div>
                <span className="flex items-center gap-2 text-xs font-semibold text-[color:var(--color-text-muted)]">
                  {canSetDates ? (
                    <>
                      <span>Due:</span>
                      <DateField
                        row={row}
                        field="dueDate"
                        label={`Due date for ${row.id}`}
                        enabled
                        emptyText="No due date set"
                        onSave={(v) => patchCap(row.id, { dueDate: v })}
                      />
                    </>
                  ) : formatDdMmYyyy(row.dueDate) ? (
                    <span>Due: {formatDdMmYyyy(row.dueDate)}</span>
                  ) : (
                    <span>No due date set</span>
                  )}
                  {isCapOverdue(row) ? (
                    <span className="rounded-full border border-[color:var(--color-danger-line)] bg-[color:var(--color-danger-bg)] px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-[color:var(--color-danger-fg)]">
                      Overdue
                    </span>
                  ) : null}
                </span>
              </div>

              <h4 className="text-base font-bold text-[color:var(--color-text-hero)] mb-4">{row.title || "-"}</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <FieldBlock label="Immediate Correction">
                  {canEdit ? (
                    <TextArea
                      defaultValue={row.immediateCorrection || ""}
                      rows={2}
                      className="text-xs"
                      placeholder="Short-term fix applied..."
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== (row.immediateCorrection || "")) {
                          patchCap(row.id, { immediateCorrection: val });
                        }
                      }}
                    />
                  ) : (
                    <p className="text-sm text-[color:var(--color-text-muted-dark)]">{row.immediateCorrection || "-"}</p>
                  )}
                </FieldBlock>
                <FieldBlock label="Root Cause">
                  {canEdit ? (
                    <TextArea
                      defaultValue={row.rootCause || ""}
                      rows={2}
                      className="text-xs"
                      placeholder="Underlying reason..."
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== (row.rootCause || "")) patchCap(row.id, { rootCause: val });
                      }}
                    />
                  ) : (
                    <p className="text-sm text-[color:var(--color-text-muted-dark)]">{row.rootCause || "-"}</p>
                  )}
                </FieldBlock>
                <FieldBlock label="Corrective Action">
                  {canEdit ? (
                    <TextArea
                      defaultValue={row.correctiveAction || ""}
                      rows={2}
                      className="text-xs"
                      placeholder="Long-term solution..."
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== (row.correctiveAction || "")) patchCap(row.id, { correctiveAction: val });
                      }}
                    />
                  ) : (
                    <p className="text-sm text-[color:var(--color-text-muted-dark)]">{row.correctiveAction || "-"}</p>
                  )}
                </FieldBlock>
                <FieldBlock label="Responsible">
                  {canEdit ? (
                    <Input
                      defaultValue={row.responsible || ""}
                      className="text-xs"
                      placeholder="Role or person..."
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== (row.responsible || "")) patchCap(row.id, { responsible: val });
                      }}
                    />
                  ) : (
                    <p className="text-sm text-[color:var(--color-text-muted-dark)]">{row.responsible || "-"}</p>
                  )}
                </FieldBlock>
                <FieldBlock label="RCA Method">
                  {canEdit ? (
                    <Input
                      defaultValue={row.rcaMethod || ""}
                      className="text-xs"
                      placeholder="e.g. 5 Whys, Fishbone"
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== (row.rcaMethod || "")) patchCap(row.id, { rcaMethod: val });
                      }}
                    />
                  ) : (
                    <p className="text-sm text-[color:var(--color-text-muted-dark)]">{row.rcaMethod || "-"}</p>
                  )}
                </FieldBlock>
                <FieldBlock label="Lessons Learned">
                  {canEdit ? (
                    <TextArea
                      defaultValue={row.lessonsLearned || ""}
                      rows={2}
                      className="text-xs"
                      placeholder="Insights for future improvement..."
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== (row.lessonsLearned || "")) patchCap(row.id, { lessonsLearned: val });
                      }}
                    />
                  ) : (
                    <p className="text-sm text-[color:var(--color-text-muted-dark)]">{row.lessonsLearned || "-"}</p>
                  )}
                </FieldBlock>
              </div>

              <div className="border-t border-[color:var(--color-border)] pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <FieldBlock label="Verification Method">
                  {canEdit ? (
                    <Input
                      defaultValue={row.verificationMethod || ""}
                      className="text-xs"
                      placeholder="How correction will be verified..."
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val !== (row.verificationMethod || "")) {
                          patchCap(row.id, { verificationMethod: val });
                        }
                      }}
                    />
                  ) : (
                    <p className="text-sm text-[color:var(--color-text-muted-dark)]">{row.verificationMethod || "-"}</p>
                  )}
                </FieldBlock>
                <FieldBlock label="Verification Due">
                  <DateField
                    row={row}
                    field="verificationDue"
                    label={`Verification due for ${row.id}`}
                    enabled={canSetDates}
                    emptyText="Not set"
                    onSave={(v) => patchCap(row.id, { verificationDue: v })}
                  />
                </FieldBlock>
                <FieldBlock label="Mgmt Approval">
                  {canEdit && canApprove ? (
                    <Select
                      size="small"
                      className="w-full"
                      value={row.mgmtApproval || "Pending"}
                      options={MGMT_OPTIONS}
                      onChange={(v) => patchCap(row.id, { mgmtApproval: v })}
                    />
                  ) : (
                    <p className="text-sm text-[color:var(--color-text-muted-dark)]">{row.mgmtApproval || "Pending"}</p>
                  )}
                </FieldBlock>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CorrectiveActionsSubTab;
